import { ModuleRegistry } from '../modules/registry';
import type { ModuleId, NavItem } from '../modules/types';
import type { PageAccess } from '../modules/pageAccess';

export interface PageEntry {
  label: string;
  path: string;
  moduleId: ModuleId;
  moduleName: string;
  moduleIcon: string;
  keywords?: string[];
}

export const KEYWORD_OVERRIDES: Record<string, string[]> = {
  '/crm/leads': ['prospects', 'pipeline', 'sales'],
  '/crm/contacts': ['people', 'directory', 'address book'],
  '/crm/outbound': ['cold calls', 'prospecting', 'outreach'],
  '/crm/follow-ups': ['proposals sent', 'follow up', 'proposal follow up'],
  '/crm/analytics': ['reports', 'metrics', 'sales data'],
  '/quoting/quotes': ['proposals', 'estimates', 'bids'],
  '/quoting/price-book': ['pricing', 'rates', 'catalog'],
  '/quoting/rate-sheet': ['labor rates', 'pricing'],
  '/quoting/follow-up-queue': ['pending', 'follow up', 'queue'],
  '/quoting/templates': ['quote templates'],
  '/quoting/equipment': ['generators', 'machines'],
  '/billing/dashboard': ['finance', 'AR', 'accounts receivable', 'billing'],
  '/billing/invoicing': ['invoices', 'invoicing', 'billing', 'AR', 'accounts receivable'],
  // Was also the "FSR Billing Queue" and "Ready to Bill" search entries (both addresses redirect here); their words live here now.
  '/billing/invoicing/service': ['service invoices', 'billing', 'field service reports', 'FSR queue', 'FSR billing queue', 'unbilled', 'ready to bill', 'reviewed tickets'],
  '/billing/invoicing/projects': ['project invoices', 'invoice matching', 'billing'],
  '/billing/collections': ['collections', 'overdue', 'aging', 'past due'],
  '/billing/tools/repricing': ['repricing', 'price updates'],
  '/billing/invoicing/link-review': ['held links', 'invoice ticket links', 'needs review'],
  '/billing/settings': ['collection settings', 'collections settings', 'finance settings'],
  '/support/dashboard': ['help desk', 'tickets', 'service requests'],
  '/support/inbox': ['tickets', 'cases', 'help desk'],
  '/command': ['executive dashboard', 'overview', 'command center'],
  '/command/operations': ['ops', 'operations dashboard'],
  '/command/sales': ['sales dashboard', 'revenue'],
  '/command/fleet': ['fleet dashboard', 'vehicles'],
  '/command/health': ['data health', 'data quality'],
  '/command/data': ['data browser', 'database', 'explorer'],
  '/assets': ['fleet', 'trucks', 'vehicles', 'equipment'],
  '/assets/inventory': ['equipment', 'asset list'],
  '/assets/trucks': ['vehicles', 'fleet', 'trucks'],
  '/assets/drivers': ['driver management', 'CDL'],
  '/assets/reports': ['fleet reports', 'asset reports'],
  '/forms': ['form builder', 'templates'],
  '/forms/templates': ['form templates', 'form builder'],
  '/forms/documents': ['document management'],
  '/forms/submissions': ['form submissions', 'completed forms'],
  '/hr': ['human resources', 'people', 'employees', 'HR dashboard'],
  '/hr/employees': ['staff', 'team', 'people', 'directory'],
  // /hr/offers now redirects to the Recruiting pipeline — hiring lives there.
  // Keeping the hiring-ish keywords pointed at the redirect means a Cmd+K
  // search for "offers" still lands somewhere that can answer it.
  '/hr/offers': ['hiring', 'job offers', 'candidates'],
  '/hr/templates': ['HR templates', 'offer letters'],
  '/hr/policies': ['compliance', 'regulations', 'policies'],
  '/hr/audit': ['audit log', 'HR audit', 'history'],
  '/projects': ['project management', 'jobs'],
  '/projects/calendar': ['project calendar', 'install calendar', 'schedule installs'],
  '/projects/equipment': ['project equipment', 'generators'],
  '/projects/invoice-matching': ['invoice matching', 'PO matching'],
  '/field-service': ['service tickets', 'work orders', 'dispatching'],
  // Also answers to the retired Projects calendar/scheduler terms — those
  // pages redirect here now, so searching for them must land somewhere.
  '/field-service/schedule': [
    'service calendar', 'scheduling', 'project calendar', 'project scheduler', 'install calendar', 'dispatch',
  ],
  '/field-service/scheduler': ['dispatch', 'planning'],
  '/field-service/schedules': ['recurring service', 'PM schedule'],
  '/field-service/generators': ['generator list', 'equipment'],
  '/field-service/contracts': ['service contracts', 'agreements'],
  '/field-service/reports': ['FSR', 'field service reports', 'review queue'],
  '/technician': ['tech', 'my jobs', 'today'],
  '/technician/history': ['job history', 'past jobs'],
  '/time': ['timesheets', 'hours', 'clock', 'time tracking'],
  '/time/timesheet-approval': ['approve timesheets', 'time approval'],
  '/time/payroll-export': ['payroll', 'pay', 'export'],
  '/time/time-off-requests': ['PTO', 'vacation', 'time off', 'leave'],
  '/recruiting': ['hiring', 'talent acquisition'],
  '/recruiting/candidates': ['applicants', 'job seekers'],
  '/recruiting/jobs': ['job postings', 'openings', 'positions'],
  '/recruiting/pipeline': ['hiring pipeline', 'recruiting funnel'],
  '/marketing': ['marketing dashboard', 'campaigns'],
  '/marketing/calendar': ['content calendar', 'editorial calendar'],
  '/marketing/ideas': ['content ideas', 'brainstorm'],
  '/marketing/composer': ['content writer', 'drafting', 'editor'],
  '/marketing/approvals': ['content approval', 'review'],
  '/marketing/media': ['media library', 'images', 'photos'],
  '/marketing/analytics': ['marketing analytics', 'metrics'],
  '/marketing/seo': ['SEO', 'search engine optimization'],
  '/marketing/seo/keywords': ['keyword research', 'SEO keywords'],
  '/marketing/seo/gbp': ['Google Business', 'GBP', 'local SEO'],
  '/marketing/seo/competitors': ['competitor analysis', 'SEO competitors'],
  '/ai-systems': ['AI dashboard', 'artificial intelligence'],
  '/ai-systems/outputs': ['AI outputs', 'model outputs'],
  '/ai-systems/usage': ['AI usage', 'token usage', 'costs'],
  '/ai-systems/discovery': ['AI discovery', 'knowledge'],
  '/ai-systems/prompts': ['prompt engineering', 'prompt library'],
  '/ai-systems/campaigns': ['AI campaigns'],
  '/ai-systems/experiments': ['A/B testing', 'experiments'],
  '/ai-systems/skills': ['AI skills', 'skill library'],
  '/ai-systems/agents': ['AI agents', 'agent inbox'],
  '/ai-systems/mailroom': ['mailroom', 'email processing'],
  '/ai-systems/deployments': ['deploy', 'ship', 'releases'],
  // Investor Relations admin pages
  '/investors/admin': ['investor admin', 'IR dashboard', 'investor overview'],
  '/investors/admin/cap-table': ['cap table', 'equity', 'shares', 'holdings', 'ownership'],
  '/investors/admin/investors': ['investor list', 'investor directory', 'shareholders'],
  '/investors/admin/library': ['investor library', 'investor documents', 'shared files'],
  '/investors/admin/updates': ['investor updates', 'investor communications', 'shareholder updates'],
  '/investors/admin/consents': ['investor consents', 'shareholder votes', 'consent manager'],
  // Investor portal pages
  '/investors': ['investor portal', 'investor dashboard'],
  '/investors/library': ['investor files', 'my documents', 'investor library'],
  '/investors/updates': ['investor news', 'investor updates'],
  '/investors/consents': ['investor voting', 'consent forms'],
  '/investors/documents': ['my investor documents', 'certificates', 'subscription agreements'],
  '/investors/profile': ['investor profile', 'my investor account'],
};

export const SUPPLEMENTAL_PAGES: PageEntry[] = [
  // CRM
  { label: 'AI Sales Assistant', path: '/crm/ai-assistant', moduleId: 'P1', moduleName: 'Sales', moduleIcon: '👥', keywords: ['AI assistant', 'sales AI', 'copilot'] },

  // Explorer / Command Center
  { label: 'Data Connections', path: '/command/connections', moduleId: 'P6', moduleName: 'Central Command', moduleIcon: '🎯', keywords: ['integrations', 'connections', 'data hub'] },
  { label: 'Collection Health', path: '/command/collection-health', moduleId: 'P6', moduleName: 'Central Command', moduleIcon: '🎯', keywords: ['collection health', 'dictionary drift', 'grading', 'triage', 'undocumented fields'] },
  { label: 'System Health', path: '/command/health', moduleId: 'P6', moduleName: 'Central Command', moduleIcon: '🎯', keywords: ['system health', 'integration health', 'heartbeat', 'uptime'] },

  // Billing
  { label: 'Collections Kanban', path: '/billing/collections/kanban', moduleId: 'P4', moduleName: 'Finance', moduleIcon: '💰', keywords: ['collections board', 'kanban', 'aging'] },

  // Fleet / Assets
  { label: 'Fleet Compliance', path: '/assets/compliance', moduleId: 'P7', moduleName: 'Assets', moduleIcon: '🚛', keywords: ['DOT', 'compliance', 'regulations'] },
  { label: 'Inspections', path: '/assets/inspections', moduleId: 'P7', moduleName: 'Assets', moduleIcon: '🚛', keywords: ['vehicle inspections', 'DVIR'] },
  { label: 'Inspection Requests', path: '/assets/inspection-requests', moduleId: 'P7', moduleName: 'Assets', moduleIcon: '🚛', keywords: ['inspection requests', 'pending inspections'] },
  { label: 'Fleet Tasks', path: '/assets/tasks', moduleId: 'P7', moduleName: 'Assets', moduleIcon: '🚛', keywords: ['maintenance tasks', 'fleet tasks'] },
  { label: 'Data Quality', path: '/assets/data-quality', moduleId: 'P7', moduleName: 'Assets', moduleIcon: '🚛', keywords: ['data quality', 'fleet data'] },
  { label: 'Policy Reconciliation', path: '/assets/policy-reconciliation', moduleId: 'P7', moduleName: 'Assets', moduleIcon: '🚛', keywords: ['insurance', 'policy audit'] },

  // HR
  { label: 'Bulk Notices', path: '/hr/notices', moduleId: 'P9', moduleName: 'HR', moduleIcon: '👔', keywords: ['notices', 'announcements', 'bulk notice'] },
  { label: 'Background Check Packages', path: '/hr/bg-packages', moduleId: 'P9', moduleName: 'HR', moduleIcon: '👔', keywords: ['background checks', 'screening'] },

  // Projects
  { label: 'Project Board', path: '/projects/board', moduleId: 'P10', moduleName: 'Projects', moduleIcon: '📋', keywords: ['kanban', 'project board'] },
  { label: 'Project Templates', path: '/projects/templates', moduleId: 'P10', moduleName: 'Projects', moduleIcon: '📋', keywords: ['project templates'] },
  { label: 'Agencies', path: '/projects/agencies', moduleId: 'P10', moduleName: 'Projects', moduleIcon: '📋', keywords: ['agencies', 'subcontractors'] },

  // AI Systems
  { label: 'AI Learning', path: '/ai-systems/learning', moduleId: 'AI_SYSTEMS', moduleName: 'AI Systems', moduleIcon: '🤖', keywords: ['learning', 'training', 'knowledge'] },
  { label: 'AI Tasks', path: '/ai-systems/tasks', moduleId: 'AI_SYSTEMS', moduleName: 'AI Systems', moduleIcon: '🤖', keywords: ['AI tasks', 'task queue'] },

  // Technician
  { label: 'My Jobs', path: '/technician/jobs', moduleId: 'P12', moduleName: 'Technician', moduleIcon: '🔧', keywords: ['jobs', 'work orders', 'assigned'] },
  { label: 'Tech Schedule', path: '/technician/schedule', moduleId: 'P12', moduleName: 'Technician', moduleIcon: '🔧', keywords: ['schedule', 'calendar'] },
  { label: 'Tech Briefings', path: '/technician/briefings', moduleId: 'P12', moduleName: 'Technician', moduleIcon: '🔧', keywords: ['briefings', 'daily briefing'] },
  { label: 'Tech Reports', path: '/technician/reports', moduleId: 'P12', moduleName: 'Technician', moduleIcon: '🔧', keywords: ['reports', 'FSR', 'field reports'] },

  // Investor Relations — investor-facing portal pages (not in top nav, only in IR sidebar)
  { label: 'Investor Dashboard', path: '/investors', moduleId: 'P18', moduleName: 'Investor Relations', moduleIcon: '🏛', keywords: ['investor portal', 'investor dashboard'] },
  { label: 'Investor Library', path: '/investors/library', moduleId: 'P18', moduleName: 'Investor Relations', moduleIcon: '🏛', keywords: ['investor files', 'investor documents', 'shared files'] },
  { label: 'Investor Updates', path: '/investors/updates', moduleId: 'P18', moduleName: 'Investor Relations', moduleIcon: '🏛', keywords: ['investor news', 'shareholder updates', 'communications'] },
  { label: 'Investor Consents', path: '/investors/consents', moduleId: 'P18', moduleName: 'Investor Relations', moduleIcon: '🏛', keywords: ['investor voting', 'consent forms', 'shareholder votes'] },
  { label: 'My Investor Documents', path: '/investors/documents', moduleId: 'P18', moduleName: 'Investor Relations', moduleIcon: '🏛', keywords: ['certificates', 'subscription agreements', 'my documents'] },
  { label: 'Investor Profile', path: '/investors/profile', moduleId: 'P18', moduleName: 'Investor Relations', moduleIcon: '🏛', keywords: ['investor account', 'my profile'] },

  // Field Service
  { label: 'FSR Reports', path: '/field-service/reports', moduleId: 'P11', moduleName: 'Field Service', moduleIcon: '🔧', keywords: ['all reports', 'FSR history', 'review queue', 'field service reports'] },
];

interface BuildOptions {
  isManagerOrAdmin: boolean;
  accessibleModules: readonly ModuleId[];
  /** May the viewer open a page with this access? Nav items with `requirePage` are skipped without it. */
  canOpenPage?: (access: PageAccess) => boolean;
}

export function buildPageRegistry(options: BuildOptions): PageEntry[] {
  const { isManagerOrAdmin, accessibleModules, canOpenPage } = options;
  const seen = new Set<string>();
  const entries: PageEntry[] = [];

  function addEntry(entry: PageEntry): void {
    if (seen.has(entry.path)) return;
    seen.add(entry.path);
    const keywords = KEYWORD_OVERRIDES[entry.path];
    entries.push(keywords ? { ...entry, keywords } : entry);
  }

  function flattenNavItems(
    items: NavItem[],
    moduleId: ModuleId,
    moduleName: string,
    moduleIcon: string,
  ): void {
    for (const item of items) {
      if (item.requireManagerOrAdmin && !isManagerOrAdmin) continue;
      // Cmd+K must not offer a page the viewer would hit a lock card on (fail closed without a predicate).
      if (item.requirePage && !(canOpenPage?.(item.requirePage) ?? false)) continue;
      if (item.children && item.children.length > 0) {
        flattenNavItems(item.children, moduleId, moduleName, moduleIcon);
      } else {
        addEntry({ label: item.label, path: item.path, moduleId, moduleName, moduleIcon });
      }
    }
  }

  const modules = ModuleRegistry.getAll();
  for (const mod of modules) {
    if (!accessibleModules.includes(mod.id)) continue;
    flattenNavItems(mod.navItems, mod.id, mod.name, mod.icon);
  }

  for (const page of SUPPLEMENTAL_PAGES) {
    if (!accessibleModules.includes(page.moduleId)) continue;
    addEntry(page);
  }

  return entries;
}
